/**
 * Hand-rolled 12-column dashboard grid (G16 verdict). Rendering only — layout
 * math lives in `utils/gridLayout.ts`, pointer/keyboard interaction in
 * `hooks/useGridInteractions.ts`. Drag starts from the tile header
 * (`[data-drag-handle]`, delegation on the wrapper); resize from the
 * bottom-right handle. In edit mode wrappers are focusable: arrows move,
 * Shift+arrows resize, and the canvas itself switches to a tinted dot-grid so
 * the mode is unmistakable (UX review: handles alone were too quiet).
 *
 * Tile removal is UNDOABLE: the ✕/kebab live in `TileFrame`, but the grid owns
 * the removal callback, so it hands the doomed tile definition to
 * `services/tileRemoveUndo.ts`, which raises the "Undo" toast.
 */
import { useMemo, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import type { DashboardTile, TileLayout } from '../types/dashboardTypes';
import { removeTileWithUndo } from '../services/tileRemoveUndo';
import { gridItemsFromTiles, GRID_COLS } from '../utils/gridLayout';
import { useGridInteractions, GRID_GAP, GRID_ROW_HEIGHT } from '../hooks/useGridInteractions';
import { TileFrame } from './TileFrame';
import { TileContent } from './tiles/TileContent';

/** Edit-mode canvas: a faint dot field over a tinted surface. `color-mix` on
 * `--foreground` keeps the dots legible in BOTH themes without a second rule. */
const EDIT_CANVAS_STYLE: CSSProperties = {
  backgroundImage:
    'radial-gradient(circle, color-mix(in oklab, var(--foreground) 22%, transparent) 1px, transparent 1px)',
  backgroundSize: '16px 16px',
};

type DashboardGridProps = {
  dashboardId: string;
  tiles: DashboardTile[];
  editMode: boolean;
  onLayoutCommit: (layouts: Record<string, TileLayout>) => unknown;
  onRemoveTile: (tileId: string) => void;
  onDuplicateTile: (tile: DashboardTile) => void;
  /** Extra kebab menu items per tile (S6/S11/S14 extension slot). */
  tileMenuItems?: (tile: DashboardTile) => ReactNode;
};

export function DashboardGrid({
  dashboardId,
  tiles,
  editMode,
  onLayoutCommit,
  onRemoveTile,
  onDuplicateTile,
  tileMenuItems,
}: DashboardGridProps) {
  const baseItems = useMemo(() => gridItemsFromTiles(tiles), [tiles]);
  // Screen-reader announcement for keyboard move/resize (C1 a11y).
  const [announcement, setAnnouncement] = useState('');
  const titleById = useMemo(() => new Map(tiles.map((t) => [t.id, t.title])), [tiles]);
  const { containerRef, items, draggingId, dragMode, dragDelta, startDrag, onTileKeyDown } =
    useGridInteractions(
      baseItems,
      editMode,
      (next) =>
        onLayoutCommit(
          Object.fromEntries(next.map((i) => [i.id, { x: i.x, y: i.y, w: i.w, h: i.h }])),
        ),
      (moved) =>
        setAnnouncement(
          `${titleById.get(moved.id) ?? 'Tile'}: row ${moved.y + 1}, column ${moved.x + 1}, ${moved.w} by ${moved.h}`,
        ),
    );
  const itemById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  // Origin cells: a MOVED tile floats from its original cell via transform, so
  // it renders there (not at its snapped target) while a drop placeholder marks
  // where it will land.
  const baseById = useMemo(() => new Map(baseItems.map((i) => [i.id, i])), [baseItems]);
  const dropTarget = dragMode === 'move' && draggingId ? itemById.get(draggingId) : undefined;

  if (tiles.length === 0) {
    return (
      <div
        className="flex flex-1 items-center justify-center p-8 text-sm text-muted-foreground"
        data-testid="dashboard-grid-empty"
      >
        No tiles yet — use “+ Tile” in the toolbar to add your first tile.
      </div>
    );
  }

  return (
    <div
      className={`flex-1 overflow-auto p-4 transition-colors ${
        editMode ? 'bg-muted/40 ring-1 ring-inset ring-primary/15' : ''
      }`}
      style={editMode ? EDIT_CANVAS_STYLE : undefined}
      data-edit-mode={editMode || undefined}
      data-testid="dashboard-grid-canvas"
    >
      <div aria-live="polite" className="sr-only" data-testid="dashboard-grid-live-region">
        {announcement}
      </div>
      <div
        ref={containerRef}
        className="grid"
        style={{
          // minmax(0, 1fr): bare `1fr` is minmax(auto, 1fr), so one tile with
          // wide intrinsic content (a table) could blow its column past the
          // container and overflow the whole grid (S24 F39 hygiene).
          gridTemplateColumns: `repeat(${GRID_COLS}, minmax(0, 1fr))`,
          gridAutoRows: `${GRID_ROW_HEIGHT}px`,
          gap: `${GRID_GAP}px`,
        }}
        data-testid="dashboard-grid"
      >
        {dropTarget && (
          // Ghost footprint of where the floating tile will land (move only).
          <div
            aria-hidden
            className="pointer-events-none rounded-lg border-2 border-dashed border-primary/50 bg-primary/5"
            style={{
              gridColumn: `${dropTarget.x + 1} / span ${dropTarget.w}`,
              gridRow: `${dropTarget.y + 1} / span ${dropTarget.h}`,
            }}
            data-testid="dashboard-drop-placeholder"
          />
        )}
        {tiles.map((tile) => {
          const item = itemById.get(tile.id);
          if (!item) return null;
          // A MOVED tile floats from its ORIGIN cell via transform (composited,
          // no per-frame layout write) while the placeholder marks its target;
          // a resized/idle tile just sits at its snapped cell.
          const isMoving = draggingId === tile.id && dragMode === 'move';
          const cell = (isMoving && baseById.get(tile.id)) || item;
          return (
            <div
              key={tile.id}
              className={`relative rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                draggingId === tile.id
                  ? isMoving
                    ? 'z-20 opacity-80 shadow-xl'
                    : 'z-10 opacity-90'
                  : ''
              }`}
              style={{
                gridColumn: `${cell.x + 1} / span ${cell.w}`,
                gridRow: `${cell.y + 1} / span ${cell.h}`,
                ...(isMoving && dragDelta
                  ? {
                      transform: `translate(${dragDelta.dx}px, ${dragDelta.dy}px)`,
                      transition: 'none',
                    }
                  : {}),
              }}
              tabIndex={editMode ? 0 : -1}
              role="group"
              aria-roledescription="dashboard tile"
              aria-label={
                editMode
                  ? `${tile.title} — arrows move, Shift+arrows resize`
                  : tile.title
              }
              data-testid="dashboard-tile-wrapper"
              data-tile-id={tile.id}
              onKeyDown={onTileKeyDown(tile.id)}
              onPointerDown={(e) => {
                if (!editMode) return;
                const target = e.target as HTMLElement;
                if (target.closest('[data-drag-handle]') && !target.closest('button')) {
                  startDrag('move', tile.id)(e);
                }
              }}
            >
              <TileFrame
                tile={tile}
                dashboardId={dashboardId}
                editMode={editMode}
                onRemove={() => removeTileWithUndo(dashboardId, tile, onRemoveTile)}
                onDuplicate={() => onDuplicateTile(tile)}
                extraMenuItems={tileMenuItems?.(tile)}
              >
                <TileContent dashboardId={dashboardId} tile={tile} />
              </TileFrame>
              {editMode && (
                <div
                  className="absolute bottom-0.5 right-0.5 h-3.5 w-3.5 cursor-se-resize rounded-sm border-b-2 border-r-2 border-muted-foreground/60 hover:border-foreground"
                  title="Resize tile"
                  data-testid="tile-resize-handle"
                  onPointerDown={startDrag('resize', tile.id)}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
